import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {
    CP_PROVISION_PENDING_BLOCK_TEXT,
    CP_PROVISION_PENDING_FIELD_TEXT
} from '../../../../../service/domain/cp-provision-pending-text';

/**
 * How to obtain a DISPLAY NAME for one FK field whose `...DtoName` does not carry one.
 *
 * The framework resolves `xDtoName` by calling `getName()` on the FK target. That works only when
 * the target HAS a name — `cp.ProvisionAgent` has no `name` column at all (`cp_provision_agent`:
 * id, agent_id, entity_status, reserve_percentage, reserve_max_amount, comment, audit) and answers
 * with a DERIVED getter over its LAZY `agent` association (`ProvisionAgent.java:299-320`). During
 * DtoName resolution the target is reloaded DETACHED, the lazy access throws, a narrow catch fires
 * and the getter returns `id.toString()`. Measured on the running E2E stack: `provisionAgentDtoName`
 * comes back as `"1"`, not `"E2E Test Agent"`.
 *
 * The name IS reachable, one hop further on: `cp.ProvisionAgent.agentDtoName` resolves correctly,
 * because `am.Agent` has a real name column. This descriptor says how to walk that hop.
 *
 * THE HONESTY RULE, and why this is a declared list rather than a best effort: a field named here
 * NEVER shows its raw value. It shows the resolved name, or it shows NOTHING. On a screen where a
 * caseworker checks data, "kein Name" is honest and a possibly wrong name is dangerous — an id
 * rendered under the label "Vermittler" reads as a name and is the more damaging of the two.
 */
export interface CpNameResolution {
    /** The rendered field whose value must be replaced, e.g. `provisionAgentDtoName`. */
    field: string;
    /** The DTO attribute holding the target id, e.g. `provisionAgentDtoId`. */
    idField: string;
    /** Entity alias of the target, e.g. `cp.ProvisionAgent`. */
    objectType: string;
    /** Attribute on the freshly read target that DOES carry the name, e.g. `agentDtoName`. */
    nameField: string;
}

/**
 * The ONE metadata-driven read pane both cp runtime OBCs are built from — and the single place
 * that implements the FD-7(a) HONESTY DUTY per FIELD.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS COMPONENT EXISTS (design gate FD-7(a), capability-matrix.md row H9 / evidence #10)
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * A manually created `cp.ProvisionContract` and the `cp.ProvisionContractAgent` rows under it
 * never pass the calculation pipeline: their run-owned fields stay empty until the NEXT
 * `ProvisionRun` fills them. The signed obligation is that each of those renders an EXPLICIT
 * German statement instead of an empty cell — per field, not as one global banner, and on BOTH
 * levels.
 *
 * WHICH fields those are is NOT decided here. This component is the mechanism; the field sets are
 * declared by the calling pane, because the test is per-field and the entity is the authority. A
 * field qualifies when it is nullable without a default AND `ProvisionCalculationService` is its
 * only writer — the same rule
 * `CpProvisionContractWizardStateService.DEFERRED_CONTRACT_AGENT_FIELDS` states, so the two
 * surfaces classify identically. The verified tables live on the panes:
 * {@link CpProvisionContractCalculationComponent} for the parent, and for the child
 * {@link CpProvisionContractAgentAmountsComponent} (six),
 * {@link CpProvisionContractAgentLiabilityComponent} (three) and
 * {@link CpProvisionContractAgentBaseDataComponent} (one).
 *
 * The framework does the opposite by default: `WidgetService.buildUiData` writes the literal
 * `'-'` into a cell whose value is null (`widget.service.ts:509-511`). A caseworker cannot tell
 * that dash apart from a bug. So the fallback has to be applied BEFORE the widget sees the data,
 * and it has to be applied on the FIELD, which is exactly what {@link applyFieldFallback} does.
 *
 * MECHANISM (no core change, no new output control registration):
 *   1. the DTO is loaded WITH its backend form (`MvsCrudService.get(id, undefined, true)`), so
 *      the field set, the labels and the order remain metadata — this is not a hand-built form;
 *   2. for every declared fallback field whose value is empty, the component writes the German
 *      text into the DTO value and RETUNES that one `MvsFormFieldDto` so the text can be rendered
 *      as text: `uiOutputControl = 'text'`, `dataType = 'java.lang.String'` and the value list
 *      handles are dropped;
 *   3. DTO and form are handed to a transient `data` widget
 *      (`WidgetFactory.createWidgetTransient(..., 'data', 'transient', 'transient', ...)`,
 *      repo precedent `bm-creditor-bank-account-basic-details.component.ts:49` and
 *      `ns-notification-object.component.ts:145`), which renders every field through
 *      `mvs-form-field-output` as usual.
 *
 * Step 2 is not cosmetic. Without `dataType` retuning, `WidgetService.buildUiData` would push the
 * German sentence of a LocalDate field through `DatePipe.transform` (`widget.service.ts:490-492`)
 * and Angular would throw on the unparsable value; without `uiOutputControl` retuning the date
 * output component would receive a sentence. Both are fixed on the cloned field of THIS pane only
 * — the entity metadata of other consumers is untouched, because `get()` issues a fresh form
 * object graph per call (documented at `mvs-crud.service.ts:429-449`).
 *
 * TWO FALLBACK KINDS, deliberately separate:
 *   * {@link pendingFields} / {@link pendingFalseFields} -> "nicht berechnet" per field, plus
 *     the block-level note {@link cPendingNote} once above the widget (FD-7(a), wording per BT-12). `pendingFalseFields` exists for `agentsResolved` alone: the column is
 *     `Boolean agentsResolved = Boolean.FALSE` (`ProvisionContract.java:487`), so it is never
 *     null and `false` can mean the not-yet-resolved state, where a flat "Nein" would read as a
 *     final answer instead of an outstanding one. It can ALSO mean "the run resolved nothing",
 *     which is a real answer — telling the two apart is the CALLER's job, not this component's;
 *     see the discriminator on {@link CpProvisionContractCalculationComponent}.
 *   * {@link dashFields} -> "—" (business rule 2). For quotas and shares a missing value means
 *     UNKNOWN and must never be shown as 0 % or 100 %.
 * A field carries at most one kind; a field that HAS a value is never touched. That is what keeps
 * the fallback honest, and it is also why declaring a NOT NULL field as pending is harmless but
 * pointless — the branch can never be reached, so such a declaration only misleads the next
 * reader. Check the column definition before adding one.
 */
@Component({
    selector: 'cp-provision-object-data',
    templateUrl: './cp-provision-object-data.component.html',
    styleUrls: ['./cp-provision-object-data.component.scss'],
    standalone: false
})
export class CpProvisionObjectDataComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * The PER-FIELD statement, taken from the module-wide constant.
     *
     * NOT a literal any more: the string lives exactly once in the module, in
     * `cp/service/domain/cp-provision-pending-text.ts`, and both surfaces that render it — these
     * detail panes and the two top-down wizards — read it from there. That makes the match a
     * COMPILER guarantee instead of two authors remembering the same sentence, which matters
     * because the acceptance test asserts the wording: had the two drifted, it would have checked
     * one surface and silently stopped covering the other.
     *
     * Kept as a static member rather than replacing every use site with the import, mirroring what
     * the wizard state service did with DEFERRED_HINT — no caller of this class had to change.
     *
     * The reasoning for the WORDING (the three empty states, and why no temporal word may come
     * back) lives in the header of that constants file. Do not restate it here, and do not re-add
     * a promise: the explanation of the NORMAL case belongs to {@link cPendingNote} at the head of
     * the block.
     */
    public static readonly cPendingText: string = CP_PROVISION_PENDING_FIELD_TEXT;

    /**
     * The BLOCK-LEVEL explanation, rendered ONCE above the widget and only when at least one field
     * was actually marked pending. It may speak about the normal case, because as a statement
     * about the BLOCK it is a general remark and not a claim about any single field.
     *
     * Same module-wide constant as {@link cPendingText} — see there.
     */
    public static readonly cPendingNote: string = CP_PROVISION_PENDING_BLOCK_TEXT;

    /** Business rule 2 — an unknown quota is an em dash, never 0 % and never 100 %. */
    public static readonly cNoValueText: string = '—';

    busy: boolean;
    initialized: boolean;

    /** Entity alias, e.g. `cp.ProvisionContractAgent`. */
    @Input() objectType: string;

    /** Id of the row to display; a falsy id renders the empty state. */
    @Input() objectId: number;

    /** Widget alias — one stable alias per pane, so a layout stays attached to it. */
    @Input() widgetAlias: string;

    /** German widget title. */
    @Input() widgetTitle: string;

    /**
     * Visible fields, passed on as the `defaultFormFields` widget param
     * (`widget.service.ts:981-1012`). FK fields are addressed by their `...DtoName` variant — the
     * `...DtoId` variant carries a `relationship` and is skipped by the layout compiler.
     */
    @Input() fields: string[];

    /** Fields that render the FD-7(a) hint while they are null/undefined/empty. */
    @Input() pendingFields: string[] = [];

    /** Fields that render the FD-7(a) hint while they are null OR `false` — see class javadoc. */
    @Input() pendingFalseFields: string[] = [];

    /**
     * FK fields whose `...DtoName` does not carry a name and must be resolved one hop further —
     * see {@link CpNameResolution}. Each entry costs ONE light read, and only on a pane that
     * declares it. A field listed here never shows its raw value.
     */
    @Input() nameResolutions: CpNameResolution[] = [];

    /**
     * Fields that render "—" while they are empty (business rule 2: an unknown quota is never
     * printed as 0 % or 100 %).
     *
     * NO CALLER USES THIS TODAY, and that is a result rather than an oversight. Its only user was
     * `appliedReservePercentage`, which HIGH-1 moved to {@link pendingFields}: a share the run has
     * not computed yet is a deferred field, and an em dash there is precisely the cell the
     * evidence assertion forbids. The input stays because the rule it serves is real — but it is
     * for a value that is genuinely UNKNOWN, never for one that is merely not computed yet. If you
     * are about to add a field here, check first whether it is run-owned; if it is, it belongs in
     * {@link pendingFields}.
     */
    @Input() dashFields: string[] = [];

    /** Fields per row. */
    @Input() columns: number = 2;

    /** German empty-state text shown when no row is selected. */
    @Input() emptyText: string = 'Kein Datensatz ausgewählt.';

    dataWidget: WidgetData;

    /** True when the row could not be loaded — rendered as an error state, never as an empty pane. */
    loadFailed: boolean;

    /**
     * Did at least one pending fallback actually fire? Drives the block-level note, so the
     * explanation appears only where something is genuinely outstanding — a note above a fully
     * calculated block would be noise, and worse, a claim about nothing.
     */
    pendingNoteVisible: boolean;

    /** The block-level note, exposed for the template. */
    readonly pendingNote: string = CpProvisionObjectDataComponent.cPendingNote;

    protected dataSubscription: Subscription;
    protected nameSubscription: Subscription;
    protected objectChangeSubscription: Subscription;
    protected statusSubscription: Subscription;
    protected jobWasRunning: boolean;
    protected jobRunning: boolean;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /** Reload as soon as this very row was written elsewhere (dialog, wizard, generic CRUD). */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === this.objectType) {
                this.refreshComponent();
            }
        });
    }

    /**
     * Reload on the running -> not-running transition of a cp job.
     *
     * This is the pane where it matters most: the deferred fields are filled by exactly that run,
     * so without this subscription the pane would keep saying "nicht berechnet" after the run
     * has already filled the values.
     */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.refreshComponent();
            }
        });
    }

    /** Loads the row WITH its form and rebuilds the widget. */
    refreshComponent(): void {

        this.loadFailed = false;

        if (!this.objectType || !this.objectId) {
            this.dataWidget = null;
            this.pendingNoteVisible = false;
            this.initialized = true;
            return;
        }

        const crudService: MvsCrudService = this.coreService.getCrudService(this.objectType);

        if (!crudService) {
            this.dataWidget = null;
            this.loadFailed = true;
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.dataSubscription?.unsubscribe();

        // includeForm = true: the form is what makes this pane metadata driven — labels, order and
        // field set come from the backend FormObject, never from this component.
        this.dataSubscription = crudService.get(this.objectId, undefined, true).subscribe({
            next: (dto: DtoDetail) => {
                // Names first, THEN the widget — so it is built exactly once, with final values.
                this.resolveNames(dto, () => {
                    this.buildWidget(dto);
                    this.busy = false;
                    this.initialized = true;
                });
            },
            error: () => {
                this.dataWidget = null;
                this.loadFailed = true;
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    /**
     * Replaces the value of every {@link nameResolutions} field with a resolved display name.
     *
     * One light read per declared field (`getLight` — no form, no resolved entities), all issued in
     * parallel and awaited together, so the widget is still built ONCE. Every failure mode ends in
     * an EMPTY value rather than the raw id: no id on the row, no service for the target, an HTTP
     * error, or a target that turns out not to carry the name either. That is the honesty rule of
     * {@link CpNameResolution} — never show a number where the label promises a name.
     */
    protected resolveNames(dto: DtoDetail, done: () => void): void {

        const resolutions: CpNameResolution[] = this.nameResolutions ?? [];

        if (!dto || resolutions.length === 0) {
            done();
            return;
        }

        const reads: Observable<void>[] = resolutions.map(resolution => {

            // Blank FIRST: whatever happens below, the raw id must not survive.
            dto[resolution.field] = '';

            const targetId: number = dto[resolution.idField];
            const crudService: MvsCrudService = targetId
                ? this.coreService.getCrudService(resolution.objectType)
                : null;

            if (!crudService) {
                return of(undefined);
            }

            return crudService.getLight(targetId).pipe(
                map((target: DtoDetail) => {
                    const name: any = target ? target[resolution.nameField] : null;
                    dto[resolution.field] = name != null ? name : '';
                }),
                catchError(() => of(undefined))
            );
        });

        this.nameSubscription?.unsubscribe();
        this.nameSubscription = forkJoin(reads).subscribe({
            next: () => done(),
            error: () => done()
        });
    }

    /** Applies the field fallbacks and wraps DTO + form into the transient data widget. */
    protected buildWidget(dto: DtoDetail): void {

        if (!dto) {
            this.dataWidget = null;
            this.loadFailed = true;
            return;
        }

        const form: MvsFormDto = <MvsFormDto>dto['form'];

        this.applyFieldFallbacks(dto, form);

        /**
         * AI: Read-only data widget of a single cp runtime row.
         *     Source: transient - the DTO and its backend form are supplied by this component,
         *     because the deferred calculation fields carry an explicit German pending hint
         *     instead of an empty cell (design gate FD-7(a)).
         */
        this.dataWidget = WidgetFactory.createWidgetTransient(
            this.widgetAlias,
            this.widgetTitle,
            'data',
            'transient',
            'transient',
            this.objectType,
            dto,
            WidgetDataParam.create('objectId', this.objectId),
            WidgetDataParam.create('defaultFormFields', this.fields ?? []),
            WidgetDataParam.create('columns', this.columns),
            WidgetDataParam.create('labelPosition', 'left'),
            WidgetDataParam.create('size', 'S')
        );
    }

    /**
     * Runs all three fallback lists over the loaded row and records whether a PENDING one fired.
     *
     * Only the two pending lists raise {@link pendingNoteVisible}; a "—" from {@link dashFields}
     * is a statement about an unknown share, not about an outstanding calculation, so it must not
     * pull the note in with it.
     */
    protected applyFieldFallbacks(dto: DtoDetail, form: MvsFormDto): void {

        let pendingApplied: boolean = false;

        for (const field of this.pendingFields ?? []) {
            pendingApplied = this.applyFieldFallback(
                dto, form, field, CpProvisionObjectDataComponent.cPendingText, false) || pendingApplied;
        }

        for (const field of this.pendingFalseFields ?? []) {
            pendingApplied = this.applyFieldFallback(
                dto, form, field, CpProvisionObjectDataComponent.cPendingText, true) || pendingApplied;
        }

        for (const field of this.dashFields ?? []) {
            this.applyFieldFallback(dto, form, field, CpProvisionObjectDataComponent.cNoValueText, false);
        }

        this.pendingNoteVisible = pendingApplied;
    }

    /**
     * The per-field fallback itself.
     *
     * Writes `text` into the DTO value and retunes the ONE form field so the text survives the
     * widget pipeline: as `text` output, as a String data type (otherwise a LocalDate field would
     * be pushed through `DatePipe` and throw) and without value-list handles (otherwise an enum
     * field would try to resolve the sentence as a key).
     *
     * A field that holds a value is left completely untouched, and so is a field the form does not
     * know — a fallback must never invent a field the entity metadata does not carry.
     *
     * @param falseIsEmpty treat `false` as empty as well, see class javadoc (`agentsResolved`)
     * @return whether the fallback actually fired — the caller uses it to decide about the
     *         block-level note, which must not appear over a block that has nothing outstanding
     */
    protected applyFieldFallback(
        dto: DtoDetail,
        form: MvsFormDto,
        field: string,
        text: string,
        falseIsEmpty: boolean): boolean {

        // `DtoDetail.form` lazily CREATES an empty MvsFormDto when none was delivered
        // (`dto.detail.ts:26-31`), so an absent `formFields` map is a real state and not an
        // impossible one — guarding on the form object alone would throw inside getFormField().
        if (!dto || !form || !form.formFields || !field) {
            return false;
        }

        const value: any = dto[field];

        const isEmpty: boolean = value === null
            || value === undefined
            || value === ''
            || (falseIsEmpty && value === false);

        if (!isEmpty) {
            return false;
        }

        const formField: MvsFormFieldDto = form.getFormField(field);

        if (!formField) {
            return false;
        }

        dto[field] = text;

        formField.uiOutputControl = 'text';
        formField.dataType = 'java.lang.String';
        formField.valueList = null;
        formField.valueListMode = null;
        formField.fieldValueListId = null;

        return true;
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes['objectType'] || changes['objectId'] || changes['fields'] || changes['nameResolutions']
            || changes['pendingFields'] || changes['pendingFalseFields'] || changes['dashFields']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.dataSubscription?.unsubscribe();
        this.nameSubscription?.unsubscribe();
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
